import { useState, useCallback } from "react";
import { ValidateRequired } from "../utils/ValidateUtils";
import { ERROR_MESSAGES, FORM_MODES } from "../config/authModalConfig/constants";

export const useFormValidation = (formConfig, modeForm) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleInputChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  }, [errors]);

  const handleInputBlur = useCallback((fieldName) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    validateField(fieldName);
  }, [formData, formConfig, modeForm]);

  const validateField = useCallback((fieldName) => {
    const field = formConfig.fields.find((f) => f.name === fieldName);
    const value = formData[fieldName] || "";

    if (field.required && !ValidateRequired(value)) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: ERROR_MESSAGES.REQUIRED,
      }));
      return false;
    }

    if (value && field.validator && !field.validator(value)) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: field.errorMessage,
      }));
      return false;
    }

    // Check password confirmation for register form and reset password form
    if (fieldName === "RePassword" && 
        (modeForm === FORM_MODES.REGISTER || modeForm === FORM_MODES.RESET_PASSWORD)) {
      if (value !== formData.password) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: ERROR_MESSAGES.PASSWORD_MISMATCH,
        }));
        return false;
      }
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
    return true;
  }, [formData, formConfig, modeForm]);

  const validateForm = useCallback(() => {
    let isValid = true;
    formConfig.fields.forEach((field) => {
      if (!validateField(field.name)) {
        isValid = false;
      }
    });
    return isValid;
  }, [formConfig, validateField]);

  const isFormValid = useCallback(() => {
    return formConfig.fields.every((field) => {
      const value = formData[field.name] || "";
      if (field.required && !ValidateRequired(value)) return false;
      if (value && field.validator && !field.validator(value)) return false;
      // Check password confirmation
      if (field.name === "RePassword" && 
          (modeForm === FORM_MODES.REGISTER || modeForm === FORM_MODES.RESET_PASSWORD)) {
        return value === formData.password;
      }
      return true;
    });
  }, [formData, formConfig, modeForm]);

  const resetForm = useCallback(() => {
    setFormData({});
    setErrors({});
    setTouched({});
  }, []);

  const setAllTouched = useCallback(() => {
    const allTouched = {};
    formConfig.fields.forEach((field) => {
      allTouched[field.name] = true;
    });
    setTouched(allTouched);
  }, [formConfig]);

  return {
    formData,
    errors,
    touched,
    handleInputChange,
    handleInputBlur,
    validateForm,
    isFormValid,
    resetForm,
    setAllTouched,
  };
};
